/**
 * Calendar date helpers.
 *
 * Orders store `date` as a local YYYY-MM-DD string. Bucketing cells with
 * `Date.toISOString().slice(0,10)` is timezone-flaky — it returns the UTC
 * date, which can shift by ±1 day depending on the user's locale. These
 * helpers operate exclusively in local time.
 */

/** Format a Date as a local YYYY-MM-DD key. */
export function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD string into a local-midnight Date. */
export function parseLocalDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** Today as a local YYYY-MM-DD key. */
export function todayLocalKey(): string {
  return localDateKey(new Date());
}

/** Sunday-anchored start of the week containing `d` (local midnight). */
export function startOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
}

/** Add `days` to `d` returning a new Date. */
export function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

/** Add `months` to `d` returning a new Date (anchored on the 1st). */
export function addMonths(d: Date, months: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + months, 1);
}
